import { FORMAT_LABELS, fmtDate } from '@jt/core';
import type { OffersCompare } from '@jt/shared';
import { useOffersCompare } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Empty, Logo, PageHead, StagePill } from '../components/ui';
import { DRAFT_TITLES, useAi, useOpenDraft } from '../drafts/useDraft';
import { ScoreView } from '../panel/OfferTab';

type Row = OffersCompare['offers'][number];

const k = (n: number | null | undefined) => (n ? `${n}k €` : '—');

function CompareTable({ offers }: { offers: Row[] }) {
  const cell = (f: (r: Row) => string) => offers.map((r) => <td key={r.application.id}>{f(r)}</td>);
  return (
    <section className="box">
      <h2>Сравнение</h2>
      <div className="tblw" style={{ border: 0 }} tabIndex={0} role="region" aria-label="Сравнение офферов">
        <table className="tbl">
          <thead>
            <tr>
              <th />
              {offers.map((r) => (
                <th key={r.application.id}>
                  <div className="tc">
                    <Logo name={r.application.company} size={26} />
                    <b>{r.application.company}</b>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ cursor: 'default' }}>
              <td>Совпадение</td>
              {offers.map((r) => (
                <td key={r.application.id}>{r.score.value == null ? '—' : <b>{r.score.value}</b>}</td>
              ))}
            </tr>
            <tr style={{ cursor: 'default' }}>
              <td>Зарплата</td>
              {cell((r) => k(r.offer?.salaryK))}
            </tr>
            <tr style={{ cursor: 'default' }}>
              <td>Бонус</td>
              {cell((r) => r.offer?.bonus || '—')}
            </tr>
            <tr style={{ cursor: 'default' }}>
              <td>Формат</td>
              {cell((r) => {
                const f = r.offer?.format || r.application.format;
                return f ? FORMAT_LABELS[f] : '—';
              })}
            </tr>
            <tr style={{ cursor: 'default' }}>
              <td>Контракт</td>
              {cell((r) => r.offer?.contract || '—')}
            </tr>
            <tr style={{ cursor: 'default' }}>
              <td>Выход</td>
              {cell((r) => fmtDate(r.offer?.startDate) || '—')}
            </tr>
            <tr style={{ cursor: 'default' }}>
              <td>Ответить до</td>
              {cell((r) => fmtDate(r.offer?.deadline) || '—')}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OfferCard({ r }: { r: Row }) {
  const ui = useUI();
  const ai = useAi();
  const openDraft = useOpenDraft();
  const a = r.application;
  const o = r.offer;
  const f = o?.format || a.format;
  const dl = r.daysLeft;
  return (
    <section className="box">
      <div className="boxh">
        <div className="tc">
          <Logo name={a.company} size={40} />
          <div>
            <b>{a.company}</b>
            <span>{a.role}</span>
          </div>
        </div>
        <StagePill stage={a.stage} />
      </div>
      <ScoreView score={r.score} />
      <div className="facts">
        <div>
          Зарплата<b>{k(o?.salaryK)}</b>
        </div>
        <div>
          Бонус<b>{o?.bonus || '—'}</b>
        </div>
        <div>
          Формат<b>{f ? FORMAT_LABELS[f] : '—'}</b>
        </div>
        <div>
          Контракт<b>{o?.contract || '—'}</b>
        </div>
        <div>
          Выход<b>{o?.startDate ? fmtDate(o.startDate) : '—'}</b>
        </div>
        <div>
          Ответить до
          <b className={dl != null && dl <= 2 ? 'hot' : ''}>
            {dl == null ? '—' : `${fmtDate(o?.deadline)}${dl >= 0 ? ` · ${dl} дн.` : ' · прошёл'}`}
          </b>
        </div>
      </div>
      <div className="btnrow">
        <button className="btn sm" onClick={() => ui.openApp(a.id)}>
          Открыть карточку
        </button>
        {a.stage === 'offer' &&
          ai &&
          (['accept', 'negotiate', 'decline'] as const).map((kind) => (
            <button key={kind} className="btn sm" onClick={() => openDraft(a.id, kind)}>
              {DRAFT_TITLES[kind]}
            </button>
          ))}
      </div>
    </section>
  );
}

export function OffersPage() {
  const q = useOffersCompare();
  const d = q.data;
  return (
    <>
      <PageHead title="Сравнение офферов" sub="Оценка по вашим критериям и готовность быстро ответить" />
      {d && d.offers.length > 1 && <CompareTable offers={d.offers} />}
      {d &&
        (d.offers.length ? (
          d.offers.map((r) => <OfferCard key={r.application.id} r={r} />)
        ) : (
          <Empty
            title="Офферов пока нет"
            text="Переведите вакансию в статус «Оффер» и заполните условия во вкладке «Оффер» — здесь появится оценка и сравнение."
          />
        ))}
    </>
  );
}
